import type { SyncUserResponse } from "../types/auth";
import { apiClient } from "./apiClient";

/**
 * Función helper para obtener token JWT de Cognito manualmente (para casos especiales)
 * Los interceptores manejan automáticamente la autenticación para todas las peticiones
 */
// Eliminado getAuthToken no utilizado; los interceptores manejan los tokens automáticamente

/**
 * Servicio para sincronizar usuario con Cognito
 * Endpoint principal para sincronización automática según planificación
 * El token JWT se agrega automáticamente via interceptores
 *
 * @returns Datos del usuario, grupos y permisos
 */
async function syncUser(): Promise<SyncUserResponse> {
  try {
    // Los interceptores agregan automáticamente el token JWT
    const response = await apiClient.post<SyncUserResponse>(
      "/auth/sync-user",
      {},
    );
    return response.data;
  } catch (error) {
    console.error("Error sincronizando usuario:", error);
    throw new Error("No se pudo sincronizar el usuario");
  }
}

/**
 * Servicio para validar si el usuario tiene un permiso específico
 * Verificación adicional desde el backend si es necesario
 * El token JWT se agrega automáticamente via interceptores
 *
 * @param ruta - Ruta del sistema
 * @param menu - Menú específico
 * @param funcion - Función específica
 * @returns true si tiene el permiso, false en caso contrario
 */
async function validatePermission(
  ruta: string,
  menu: string,
  funcion: string,
): Promise<boolean> {
  try {
    // Los interceptores agregan automáticamente el token JWT
    const response = await apiClient.get<{ hasPermission: boolean }>(
      `/auth/validate-permission`,
      {
        params: { ruta, menu, funcion },
      },
    );

    return response.data.hasPermission;
  } catch (error) {
    console.error("Error validando permiso:", error);
    return false;
  }
}

/**
 * Servicio para refrescar los datos del usuario
 * Útil cuando se actualizan permisos o grupos
 *
 * @returns Datos actualizados del usuario
 */
async function refreshUserData(): Promise<SyncUserResponse> {
  return syncUser();
}

// Exportar objeto con todos los servicios
export const authService = {
  syncUser,
  validatePermission,
  refreshUserData,
};

// Exportar apiClient configurado con interceptores automáticos
// Todas las peticiones incluyen automáticamente el token JWT de Cognito
export { apiClient };
